import { create } from "zustand";
import { toast } from "@components/ui/toaster";
import { uploadService } from "@services/upload";

type UploadStore = {
	uploads: Map<string, UploadItem>;
	maxActiveUploads: number;
	startUpload: (uuid: string, file: File, uploadUrl: string) => void;
	resumeUpload: (uuid: string) => void;
	stopUpload: (uuid: string) => void;
	deleteUpload: (uuid: string) => void;
	getUpload: (uuid: string) => UploadItem | undefined;
	getAllUploads: () => UploadItem[];
	getAllActiveUploads: () => UploadItem[];
	getAllCompletedUploads: () => UploadItem[];
	getAllFailedUploads: () => UploadItem[];
};

export const useUploadStore = create<UploadStore>((set, get) => ({
	uploads: new Map(),
	maxActiveUploads: 3,

	startUpload: (uuid: string, file: File, uploadUrl: string) => {
		const { getUpload, getAllActiveUploads, maxActiveUploads } = get();
		if (getUpload(uuid)) {
			toast.error("Error!", { description: "Upload already exists!" });
			return;
		}

		if (getAllActiveUploads().length >= maxActiveUploads) {
			toast.error("Error!", { description: `Max ${maxActiveUploads} active uploads allowed!` });
			return;
		}

		const uploadTask = uploadService.startUpload(uuid, file, uploadUrl);

		set(({ uploads }) => {
			const newUploads = new Map(uploads).set(uuid, uploadTask);
			return { uploads: newUploads };
		});

		const interval = setInterval(() => {
			const uploadItem = getUpload(uuid);

			if (!uploadItem || !uploadTask) {
				clearInterval(interval);
				return;
			}

			set(({ uploads }) => {
				const newUploads = new Map(uploads);
				const upload = newUploads.get(uuid);
				if (upload) {
					newUploads.set(uuid, { ...uploadTask });
				}
				return { uploads: newUploads };
			});

			if (uploadTask.status === "COMPLETED") {
				toast.success("Success!", { description: "Upload completed successfully!" });
				clearInterval(interval);
			}

			if (uploadTask.status === "ERROR") {
				toast.error("Error!", { description: uploadTask?.error || "Upload failed!" });
				clearInterval(interval);
			}
		}, 100);

		toast.success("Success!", { description: "Upload started successfully!" });
	},

	resumeUpload: (uuid: string) => {
		uploadService.resumeUpload(uuid);
		set(({ uploads }) => {
			const newUploads = new Map(uploads);
			const upload = newUploads.get(uuid);
			if (upload) {
				newUploads.set(uuid, { ...upload, status: "UPLOADING" });
			}
			return { uploads: newUploads };
		});
	},

	stopUpload: (uuid: string) => {
		uploadService.stopUpload(uuid);
		set(({ uploads }) => {
			const newUploads = new Map(uploads);
			const upload = newUploads.get(uuid);
			if (upload) {
				newUploads.set(uuid, { ...upload, status: "STOPPED" });
			}
			return { uploads: newUploads };
		});
	},

	deleteUpload: (uuid: string) => {
		uploadService.deleteUpload(uuid);
		set(({ uploads }) => {
			const newUploads = new Map(uploads);
			newUploads.delete(uuid);
			return { uploads: newUploads };
		});
	},

	getUpload: (uuid: string) => {
		const { uploads } = get();
		return uploads.get(uuid);
	},

	getAllUploads: () => {
		const { uploads } = get();
		return Array.from(uploads.values());
	},

	getAllActiveUploads: () => {
		const { getAllUploads } = get();
		return getAllUploads().filter(upload => upload.status === "UPLOADING");
	},

	getAllCompletedUploads: () => {
		const { getAllUploads } = get();
		return getAllUploads().filter(upload => upload.status === "COMPLETED");
	},

	getAllFailedUploads: () => {
		const { getAllUploads } = get();
		return getAllUploads().filter(upload => upload.status === "ERROR");
	},
}));
